/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 *
 * CaseViewV2 — the redesigned case screen chrome (v1_402).
 *
 * Presentation layer only. Every value shown here is derived in
 * MLRODashboard.jsx from the same state the classic view reads, and every
 * action calls the same handlers the classic view calls. Nothing in this
 * file writes to the database directly and nothing changes the engine:
 * analysis proposes, officer disposes, and this file only arranges the
 * proposal. The classic view remains one click away and untouched.
 *
 * Zones follow the approved v5 prototype: Judgment (teal) is what the
 * platform concluded and what needs a decision; the evidence panels below
 * are the existing panels, unchanged. The sidebar is a map of everything
 * assessed, whether or not it found anything.
 */

import { useState, useEffect, useRef } from "react";
import { stageAttr } from "./stage";
import { plainText } from '../shared/plain-text.js';

/* ── Theme tokens ─────────────────────────────────────────────────────
   Two themes, one token sheet, scoped to .cidv2 so the classic view is
   untouched. The chrome (sidebar) stays deep navy in both themes: that
   is the signature. Evidence panels adopt the theme as they migrate. */
const CSS = `
.cidv2{
  --v2bg:transparent;--v2panel:#0b1e33;--v2panel2:#0d2338;--v2modal:#0a1c2e;
  --v2input:rgba(0,0,0,.3);
  --v2line:rgba(255,255,255,.08);--v2soft:rgba(255,255,255,.07);--v2hover:rgba(255,255,255,.03);
  --v2tx:#ecece6;--v2tx2:rgba(255,255,255,.84);--v2tx3:rgba(255,255,255,.76);
  --v2mut:rgba(255,255,255,.62);--v2mut2:rgba(255,255,255,.42);--v2onaccent:#062033;
  --v2teal:#13a4a4;--v2tealx:#84f1ed;
  --v2amber:#f59e0b;--v2ambertx:#f5a623;
  --v2red:#ef4444;--v2redtx:#fca5a5;--v2redhd:#fecaca;
  --v2teal-tint:rgba(19,164,164,.08);--v2teal-chip:rgba(19,164,164,.13);--v2teal-line:rgba(19,164,164,.38);
  --v2amber-tint:rgba(245,158,11,.07);--v2amber-chip:rgba(245,158,11,.16);--v2amber-line:rgba(245,158,11,.4);
  --v2red-tint:rgba(239,68,68,.08);--v2red-chip:rgba(239,68,68,.14);--v2red-line:rgba(239,68,68,.42);
  font-size:14px;line-height:1.55;color:var(--v2tx)}
.cidv2[data-theme="light"]{
  --v2bg:#EDF1F6;--v2panel:#FFFFFF;--v2panel2:#F3F6F9;--v2modal:#FFFFFF;--v2input:#F5F7FA;
  --v2line:#D8E1EA;--v2soft:rgba(16,42,67,.07);--v2hover:rgba(16,42,67,.045);
  --v2tx:#102A43;--v2tx2:rgba(16,42,67,.92);--v2tx3:rgba(16,42,67,.82);
  --v2mut:#486581;--v2mut2:#7C8DA0;--v2onaccent:#FFFFFF;
  --v2teal:#0F8B8D;--v2tealx:#0A6B6D;
  --v2amber:#C9750B;--v2ambertx:#9A6006;
  --v2red:#C53030;--v2redtx:#A82525;--v2redhd:#7F1D1D;
  --v2teal-tint:rgba(15,139,141,.08);--v2teal-chip:rgba(15,139,141,.13);--v2teal-line:rgba(15,139,141,.45);
  --v2amber-tint:rgba(201,117,11,.09);--v2amber-chip:rgba(201,117,11,.15);--v2amber-line:rgba(201,117,11,.45);
  --v2red-tint:rgba(197,48,48,.06);--v2red-chip:rgba(197,48,48,.12);--v2red-line:rgba(197,48,48,.42)}
.cidv2[data-theme="light"] .v2rowbody{background:#0b1c30;border-radius:10px;padding:10px 12px}
.cidv2[data-theme="light"] .v2card,.cidv2[data-theme="light"] .v2now,
.cidv2[data-theme="light"] .v2blocker,.cidv2[data-theme="light"] .v2sp{box-shadow:0 1px 3px rgba(16,36,56,.07)}

/* Sidebar chrome: constant deep navy in both themes */
.cidv2-side{position:fixed;left:0;top:0;bottom:0;width:258px;z-index:60;overflow-y:auto;
  background:#061422;border-right:1px solid rgba(255,255,255,.1);padding:14px 0 40px;
  padding-top:calc(var(--cidv2-hdr-h, 56px) + 14px);
  font-size:14px;line-height:1.5}
.cidv2-side h1{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#84f1ed;
  margin:0 0 3px;padding:0 16px;font-weight:700;line-height:1.35}
.cidv2-side .v2sub{font-size:11px;color:rgba(255,255,255,.42);padding:0 16px;margin-bottom:12px}
.cidv2-side .v2legend{display:flex;gap:12px;padding:0 16px 12px;font-size:10px;color:rgba(255,255,255,.42)}
.cidv2-side .v2legend span{display:flex;align-items:center;gap:5px}
.v2grp>button{width:100%;display:flex;align-items:center;gap:8px;background:none;border:0;color:#ecece6;
  font:inherit;font-size:11.5px;font-weight:600;padding:8px 16px;cursor:pointer;text-align:left}
.v2grp>button:hover{background:rgba(255,255,255,.04)}
.v2grp .v2caret{color:rgba(255,255,255,.42);font-size:9px;width:8px;transition:transform .15s}
.v2grp.open .v2caret{transform:rotate(90deg)}
.v2grp .v2cnt{margin-left:auto;font-size:10px;color:rgba(255,255,255,.42)}
.v2grp .v2cnt.a{color:#f59e0b}
.v2items{display:none;padding:2px 0 8px}.v2grp.open .v2items{display:block}
.v2items button{width:100%;display:flex;align-items:center;gap:8px;padding:5px 16px 5px 32px;font-size:11.5px;
  color:rgba(255,255,255,.62);background:none;border:0;border-left:2px solid transparent;cursor:pointer;
  font-family:inherit;text-align:left}
.v2items button:hover{color:#ecece6;background:rgba(255,255,255,.04)}
.v2dot{width:6px;height:6px;border-radius:50%;flex:0 0 6px;background:#13a4a4}
.v2dot.a{background:#f59e0b}.v2dot.r{background:#ef4444}.v2dot.n{background:rgba(255,255,255,.16)}
.cidv2-sidefoot{padding:14px 16px 0;margin-top:10px;border-top:1px solid rgba(255,255,255,.1);
  font-size:10.5px;color:rgba(255,255,255,.42);line-height:1.5}
.cidv2-sidefoot button{background:none;border:0;color:rgba(255,255,255,.42);font-size:10.5px;cursor:pointer;
  font-family:inherit;padding:0;margin-top:8px;text-decoration:underline;text-underline-offset:3px;display:block}
.cidv2-sidefoot button:hover{color:rgba(255,255,255,.7)}
@media(max-width:1150px){.cidv2-side{display:none}}

.v2card{background:var(--v2panel);border:1px solid var(--v2line);border-radius:10px;padding:15px 17px;
  margin-bottom:12px;border-left:3px solid var(--v2teal);scroll-margin-top:90px}
.v2card h3{margin:0;font-size:10.5px;letter-spacing:.11em;text-transform:uppercase;color:var(--v2tealx)}
.v2hd{display:flex;align-items:baseline;gap:12px}
.v2hd .v2cnt{margin-left:auto;font-size:11px;color:var(--v2mut2)}
.v2subline{font-size:12px;color:var(--v2mut);margin:3px 0 10px}

.v2spine{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:12px}
.v2sp{border:1px solid var(--v2line);border-radius:9px;padding:11px 13px;background:var(--v2panel)}
.v2sp .n{font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--v2mut2);font-weight:700;
  display:flex;align-items:center;gap:7px}
.v2sp .num{width:17px;height:17px;border-radius:50%;display:grid;place-items:center;font-size:9.5px;
  background:var(--v2soft);color:var(--v2mut2)}
.v2sp p{margin:6px 0 0;font-size:12px;color:var(--v2mut);line-height:1.5}
.v2sp.on{border-color:var(--v2amber-line);background:linear-gradient(var(--v2amber-tint),var(--v2amber-tint)),var(--v2panel)}
.v2sp.on .n{color:var(--v2amber)}.v2sp.on .num{background:var(--v2amber);color:var(--v2onaccent)}
.v2sp.on p{color:var(--v2tx2)}
.v2sp.done{border-color:var(--v2teal-line)}
.v2sp.done .n{color:var(--v2teal)}.v2sp.done .num{background:var(--v2teal);color:var(--v2onaccent)}
.v2sp .here{margin-left:auto;font-size:9px;letter-spacing:.06em;padding:2px 7px;border-radius:999px;
  background:var(--v2amber-chip);color:var(--v2amber)}
.v2rule{font-size:10.5px;color:var(--v2mut2);margin:-4px 0 12px;text-align:right}

.v2now{background:linear-gradient(var(--v2amber-tint),var(--v2amber-tint)),var(--v2panel);border:2px solid var(--v2amber-line);box-shadow:0 0 0 1px rgba(245,158,11,.12),0 4px 18px rgba(0,0,0,.25);border-radius:10px;
  padding:13px 15px;margin-bottom:12px;display:flex;gap:14px;align-items:flex-start;position:relative;overflow:hidden}
/* This is the box that tells the officer what the case needs NEXT, so the eye
   should land on it before anything else. A slow highlight travels the border
   once every few seconds: enough to draw attention, not enough to nag. It
   stops for anyone who has asked for reduced motion. */
.v2now::after{content:"";position:absolute;inset:-2px;border-radius:10px;padding:2px;pointer-events:none;
  background:conic-gradient(from var(--v2spin,0deg),rgba(245,158,11,0) 0deg,rgba(245,158,11,0) 250deg,rgba(245,158,11,.85) 300deg,rgba(255,214,140,.95) 330deg,rgba(245,158,11,0) 360deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);mask-composite:exclude;
  animation:v2spin 5.5s linear infinite}
@property --v2spin{syntax:'<angle>';inherits:false;initial-value:0deg}
@keyframes v2spin{to{--v2spin:360deg}}
@media (prefers-reduced-motion:reduce){.v2now::after{animation:none;background:none}}
.v2now.go{background:linear-gradient(var(--v2teal-tint),var(--v2teal-tint)),var(--v2panel);border-color:var(--v2teal-line);box-shadow:0 0 0 1px rgba(19,164,164,.12),0 4px 18px rgba(0,0,0,.25)}
.v2now .k{font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:var(--v2amber);font-weight:700}
.v2now.go .k{color:var(--v2tealx)}
.v2now .big{font-size:14.5px;font-weight:600;margin-top:2px;color:var(--v2tx)}
.v2now .sm{font-size:12.5px;color:var(--v2mut);margin-top:2px;max-width:72ch}
.v2spacer{flex:1}
.v2btn{background:var(--v2teal);border:0;color:var(--v2onaccent);font-weight:700;font-size:12px;border-radius:7px;
  padding:8px 15px;cursor:pointer;white-space:nowrap;font-family:inherit}
.v2btn.ghost{background:none;border:1px solid var(--v2line);color:var(--v2mut);font-weight:500}
.v2btn.ghost:hover{color:var(--v2tx)}
.v2btn[disabled]{opacity:.35;cursor:not-allowed}

.v2blocker{border:1px solid var(--v2red-line);background:linear-gradient(var(--v2red-tint),var(--v2red-tint)),var(--v2panel);border-radius:10px;
  padding:13px 16px;margin-bottom:12px;border-left:3px solid var(--v2red)}
.v2blocker .k{font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:var(--v2redtx);font-weight:700}
.v2blocker .big{font-size:14px;font-weight:650;margin-top:3px;color:var(--v2redhd)}
.v2blocker .sm{font-size:12.5px;color:var(--v2tx3);margin-top:4px;max-width:78ch}
.v2blocker .v2bact{margin-top:8px}

.v2chk{display:grid;grid-template-columns:18px 1fr auto;gap:11px;align-items:baseline;padding:7px 0;
  border-top:1px solid var(--v2soft);font-size:12.5px}
.v2chk:first-child{border-top:0}
.v2chk .tick{font-size:12px;line-height:1.4}
.v2chk.ok .tick{color:var(--v2teal)}.v2chk.no .tick{color:var(--v2red)}
.v2chk .q{font-weight:550;color:var(--v2tx)}
.v2chk .a{display:block;font-size:11.5px;color:var(--v2mut);margin-top:1px}
.v2chk.no .a{color:var(--v2ambertx)}
.v2tag{font-size:9px;letter-spacing:.06em;text-transform:uppercase;font-weight:700;
  border-radius:4px;padding:1px 6px;margin-left:7px;vertical-align:1px}
.v2tag.b{color:var(--v2redtx);background:var(--v2red-chip)}
.v2tag.p{color:var(--v2ambertx);background:var(--v2amber-chip)}
.v2chk .jump{font-size:10.5px;color:var(--v2tealx);background:none;border:1px solid var(--v2teal-line);
  border-radius:5px;padding:3px 9px;cursor:pointer;font-family:inherit;white-space:nowrap}
.v2chk.ok .jump{visibility:hidden}
.v2boardfoot{font-size:12.5px;color:var(--v2tx3);margin-top:12px;padding-top:11px;border-top:1px solid var(--v2line)}
.v2decide{display:flex;gap:9px;margin-top:12px;padding-top:12px;border-top:1px solid var(--v2line);
  align-items:center;flex-wrap:wrap}
.v2decide .txt{font-size:11.5px;color:var(--v2mut);margin-right:auto;max-width:56ch}
.v2btn.dec{background:none;border:1px solid var(--v2red-line);color:var(--v2redtx)}
.v2btn.req{background:none;border:1px solid var(--v2amber-line);color:var(--v2amber)}
.v2ovlink{display:block;font-size:10px;color:var(--v2mut2);margin-top:3px;background:none;border:0;
  cursor:pointer;font-family:inherit;padding:0;text-decoration:underline;text-underline-offset:2px;text-align:left}
.v2ovlink:hover{color:var(--v2mut)}

.v2whohd{font-size:10.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--v2mut2);margin:10px 0 2px}
ul.v2acts{list-style:none;margin:0;padding:0}
ul.v2acts li{display:grid;grid-template-columns:1fr auto;gap:12px;align-items:baseline;padding:7px 0;
  border-top:1px solid var(--v2soft);font-size:12.5px;color:var(--v2tx)}
ul.v2acts li:first-child{border-top:0}
ul.v2acts .res{display:block;font-size:11.5px;color:var(--v2mut);margin-top:2px}
ul.v2acts .go{font-size:10.5px;color:var(--v2tealx);background:none;border:1px solid var(--v2teal-line);
  border-radius:5px;padding:3px 10px;cursor:pointer;font-family:inherit;white-space:nowrap}
ul.v2acts.wait li,ul.v2acts.wait .res{color:var(--v2mut2)}
ul.v2acts .stat{font-size:10.5px;color:var(--v2mut2);white-space:nowrap}
.v2lanenote{font-size:11.5px;color:var(--v2mut2);padding:6px 0}

.cidv2-main{display:contents}
/* v1_673 redesign — one hoisted flex column, every panel an explicit order.
   Bands (lower = higher on the page): alert banners < orientation < decision
   headline < action bar < outstanding < risk factors < decision board < AI
   review detail < money map < [VERIFICATION header + panels] < [EVIDENCE header
   + panels] < MLRO decision. EvidenceRow wrappers take their order from
   v2meta/V2_ORDER (inline, wins over these id rules); the rules here drive the
   CaseViewV2-native cards only. The structural section headers (VERIFICATION,
   EVIDENCE & RECORD) and the Decision headline are rendered in the report body
   outside .cidv2 with inline orders -11 / -1 / -100. */
.v2verdict{order:-110}
.v2brief{order:-110}
.v2briefsum{font-size:12.5px;color:var(--v2tx2);line-height:1.6;max-width:92ch}
.v2briefdoms{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;margin-top:10px}
.v2dom{border:1px solid var(--v2line);border-radius:8px;padding:7px 10px;background:var(--v2panel2)}
.v2dom .k{font-size:9.5px;letter-spacing:.07em;text-transform:uppercase;color:var(--v2mut2)}
.v2dom .v{font-size:11.5px;color:var(--v2tealx);margin-top:2px}
.v2dom.warn .v{color:var(--v2ambertx)}
.v2briefpos{display:flex;flex-wrap:wrap;gap:5px 16px;margin-top:10px;font-size:11.5px;color:var(--v2tealx)}
.v2briefnote{font-size:10.5px;color:var(--v2mut2);margin-top:10px}
.v2workguide{font-size:12px;color:var(--v2tx2);line-height:1.6;border-left:3px solid var(--v2teal)}
.v2workguide b{color:var(--v2tx)}
/* Action bar (v1_673) — one flex child holding the stage spine, NowBlock, the
   secondary action palette, the dismissible workguide and the task lanes. It is
   a block container, so its children stack in DOM order (primary NowBlock above
   the secondary palette) and their own order values are inert inside it. */
.v2actionbar{order:-90;margin-bottom:12px;background:var(--v2panel);border:1px solid var(--v2line);border-radius:10px;overflow:hidden}
.v2ab-sec{padding:13px 17px}
.v2ab-sec + .v2ab-sec{border-top:1px solid var(--v2line)}
/* strip the inner card chrome so the pieces read as sub-sections of the one bar,
   not separate panels. The stage tiles (.v2sp) keep their own borders. */
.v2actionbar .v2now,.v2actionbar .v2card{background:transparent;border:0;box-shadow:none;border-radius:0;padding:0;margin:0}
.v2actionbar .v2now::after{display:none}
.v2actionbar .v2spine{margin-bottom:0}
.v2actionbar .v2rule{margin:7px 0 0}
.v2actionbar .v2stageacts{margin:0}
/* one action row: the stage-aware primary, then the muted palette */
.v2ab-actions{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:11px}
/* light divider between "What to do next" and "Running automatically" */
.v2actionbar #panel-running-v2{border-top:1px solid var(--v2line);margin-top:12px;padding-top:12px}
/* Risk factors band + decision board (stage-aware: surfaces at Decide). */
#panel-rf-v2{order:-70}
/* v1_721 #4.1: in Gather the officer triages risk FIRST — lift Risk factors above
   the stage panel (-90) and Requests to send (-80), just under the case flow map
   (-95). Work/Decide keep -70 (reference, below the working panels). */
.cidv2[data-stage="gather"] #panel-rf-v2{order:-92}
/* v1_721 #4.4: the officer's own to-do sits between the stage panel and Requests
   to send — the internal half of "what to do before parking". */
#panel-actions-before-parking{order:-85}
#panel-decision-v2{order:-60}
.cidv2[data-stage="decide"] #panel-decision-v2{order:-98}
.v2morelink{background:none;border:0;color:var(--v2tealx);font-size:11px;cursor:pointer;font-family:inherit;padding:4px 0;display:block}
.v2rowwrap{margin-bottom:10px}
.v2rowbtn{width:100%;display:grid;grid-template-columns:9px minmax(160px,224px) 1fr auto;gap:13px;align-items:baseline;
  background:var(--v2panel);border:1px solid var(--v2line);border-radius:10px;color:var(--v2tx);
  font:inherit;text-align:left;padding:10px 15px;cursor:pointer;font-family:inherit}
.v2rowbtn:hover{background:var(--v2panel2)}
.v2rowbtn .nm{font-size:12.5px;font-weight:550}
.v2rowbtn .vd{font-size:12.3px;color:var(--v2tx2);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.v2rowbtn .oc{font-size:10.5px;color:var(--v2mut2);white-space:nowrap}
.v2rowwrap.attn .v2rowbtn{background:linear-gradient(var(--v2amber-tint),var(--v2amber-tint)),var(--v2panel)}
.v2rowwrap.attn .vd{color:var(--v2ambertx)}
.v2rowwrap.bad .v2rowbtn{background:linear-gradient(var(--v2red-tint),var(--v2red-tint)),var(--v2panel)}
.v2rowwrap.bad .vd{color:var(--v2redtx)}
.v2rowbody{margin-top:8px}
@media(max-width:900px){.v2rowbtn{grid-template-columns:9px 1fr}.v2rowbtn .vd,.v2rowbtn .oc{grid-column:2}}

/* Header (v1_407): replaces the legacy top bar in the v2 case view */
.cidv2-hdr{position:fixed;top:0;left:0;right:0;z-index:400;display:flex;align-items:center;gap:14px;
  padding:9px 20px;background:#050f1c;border-bottom:1px solid rgba(255,255,255,.1);
  font-size:14px;color:#ecece6}
/* Anchored scrolls stop below the fixed header, whatever its height (14/10/2026). */
html{scroll-padding-top:calc(var(--cidv2-hdr-h, 56px) + 12px)}
.cidv2-hdr .hback{font-size:11.5px;color:rgba(255,255,255,.62);background:none;border:1px solid rgba(255,255,255,.14);
  border-radius:6px;padding:5px 11px;cursor:pointer;font-family:inherit;white-space:nowrap}
.cidv2-hdr .hback:hover{color:#ecece6}
.cidv2-hdr .htabs{display:flex;gap:4px}
.cidv2-hdr .htabs button{font-size:11.5px;color:rgba(255,255,255,.62);background:none;border:1px solid transparent;
  border-radius:6px;padding:4px 11px;cursor:pointer;font-family:inherit;white-space:nowrap}
.cidv2-hdr .htabs button:hover{color:#ecece6;background:rgba(255,255,255,.04)}
.cidv2-hdr .htabs button.on{color:#84f1ed;background:rgba(19,164,164,.12);border-color:rgba(19,164,164,.4)}
.cidv2-hdr .hchip{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;
  padding:3px 11px;border-radius:999px;border:1px solid;white-space:nowrap}
.cidv2-hdr .hchip.hi{color:#fca5a5;border-color:rgba(239,68,68,.45);background:rgba(239,68,68,.1)}
.cidv2-hdr .hchip.md{color:#f5b455;border-color:rgba(245,158,11,.45);background:rgba(245,158,11,.1)}
.cidv2-hdr .hchip.ok{color:#84f1ed;border-color:rgba(19,164,164,.45);background:rgba(19,164,164,.12)}
.cidv2-hdr .hspace{flex:1}
.cidv2-hdr .hbtn{font-size:11.5px;color:#84f1ed;background:rgba(19,164,164,.1);border:1px solid rgba(19,164,164,.4);
  border-radius:6px;padding:5px 12px;cursor:pointer;font-family:inherit;white-space:nowrap}
.cidv2-hdr .hbtn.q{color:rgba(255,255,255,.62);background:none;border-color:rgba(255,255,255,.14)}
.cidv2-hdr .hbtn.q:hover{color:#ecece6}
.cidv2-hdr .hwho{font-size:11.5px;color:rgba(255,255,255,.45);white-space:nowrap}
.cidv2-hdr .hexp{position:relative}
.cidv2-hdr .hexp summary{list-style:none;cursor:pointer;font-size:11.5px;color:rgba(255,255,255,.62);padding:4px 11px;border-radius:6px;border:1px solid transparent;white-space:nowrap}
.cidv2-hdr .hexp summary:hover{color:#ecece6;background:rgba(255,255,255,.04)}
.cidv2-hdr .hexp[open] summary{color:#84f1ed;background:rgba(19,164,164,.12);border-color:rgba(19,164,164,.4)}
.cidv2-hdr .hexp summary::-webkit-details-marker{display:none}
.cidv2-hdr .hmenu{position:absolute;top:calc(100% + 8px);right:0;min-width:230px;z-index:60;
  background:#0a1c2e;border:1px solid rgba(255,255,255,.12);border-radius:9px;padding:6px}
.cidv2-hdr .hmenu button{display:block;width:100%;text-align:left;font-size:11.5px;color:rgba(255,255,255,.62);
  background:none;border:0;padding:7px 10px;border-radius:6px;cursor:pointer;font-family:inherit}
.cidv2-hdr .hmenu button:hover{color:#ecece6;background:rgba(255,255,255,.05)}
.v2verdict{font-size:11.5px;color:var(--v2mut);margin:0 0 10px;padding:7px 12px;border:1px solid var(--v2line);border-radius:8px;background:var(--v2panel)}
.v2verdict b{color:var(--v2tx);font-weight:600}
.v2stageacts{display:flex;gap:8px;margin:-2px 0 12px;flex-wrap:wrap;align-items:center}
.v2stageacts .rule{font-size:10.5px;color:var(--v2mut2);margin-left:auto}

/* Tier panel */
.v2tier{display:flex;align-items:baseline;gap:12px;margin:4px 0 8px}
.v2tier b{font-size:28px;font-weight:700;line-height:1}
.v2tier b.hi{color:var(--v2red)}.v2tier b.md{color:var(--v2amber)}.v2tier b.lo{color:var(--v2teal)}
.v2tier span{font-size:12px;color:var(--v2mut)}
.v2tierline{font-size:12px;color:var(--v2tx2);margin-bottom:9px}
.v2bars{display:grid;grid-template-columns:190px 74px 1fr;gap:6px 12px;align-items:center;font-size:12px}
.v2bars .lbl{color:var(--v2mut)}
.v2seg{display:flex;gap:3px}.v2seg i{height:4px;width:19px;border-radius:2px;background:var(--v2soft)}
.v2seg i.l{background:var(--v2teal)}.v2seg i.m{background:var(--v2amber)}.v2seg i.h{background:var(--v2red)}
.v2bars .bs{color:var(--v2tx3);font-size:11.5px}
.v2gate{margin-top:11px;padding:9px 12px;border-radius:7px;font-size:12px;
  background:linear-gradient(var(--v2red-tint),var(--v2red-tint)),var(--v2panel);
  border:1px solid var(--v2red-line);color:var(--v2redtx)}
.v2tiernote{font-size:10.5px;color:var(--v2mut2);margin-top:9px;max-width:78ch}

/* Risk factors */
.v2f{border-top:1px solid var(--v2soft)}.v2f:first-of-type{border-top:0}
.v2f>button{width:100%;display:grid;grid-template-columns:74px 1fr auto;gap:12px;align-items:baseline;
  background:none;border:0;color:var(--v2tx);font:inherit;text-align:left;padding:9px 0;cursor:pointer;font-family:inherit}
.v2pill{font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;padding:2px 0;
  border-radius:4px;text-align:center;display:block}
.v2pill.rev{color:var(--v2amber);background:var(--v2amber-chip)}
.v2pill.done{color:var(--v2mut);background:var(--v2soft)}
.v2pill.blk{color:var(--v2redtx);background:var(--v2red-chip)}
.v2f .hl{font-size:12.8px;line-height:1.5}
.v2f.done .hl{color:var(--v2mut)}
.v2f .more{font-size:10px;color:var(--v2mut2);margin-left:6px}
.v2ackbtn{font-size:10.5px;color:var(--v2tealx);background:none;border:1px solid var(--v2teal-line);
  border-radius:5px;padding:3px 10px;cursor:pointer;font-family:inherit;white-space:nowrap}
.v2f.done .v2ackbtn{color:var(--v2mut2);border-color:var(--v2line)}
.v2fdet{display:none;padding:0 0 11px 86px;font-size:12px;color:var(--v2tx3)}
.v2f.open .v2fdet{display:block}
.v2fdet .wn{font-size:11.5px;color:var(--v2tealx);margin-top:7px}
.v2fsplit{margin:12px 0 3px;padding-top:11px;border-top:1px solid var(--v2line);
  font-size:10.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--v2mut2)}
.v2fnoted{font-size:12px;color:var(--v2mut);padding:5px 0;border-top:1px solid var(--v2soft)}

/* Fixed decision bar */
.cidv2-bar{position:fixed;left:258px;right:0;bottom:0;z-index:57;display:flex;gap:10px;align-items:center;
  padding:11px 28px;background:rgba(6,20,34,.96);border-top:1px solid rgba(255,255,255,.1);
  backdrop-filter:blur(6px)}
.cidv2-bar .btxt{font-size:11.5px;color:rgba(255,255,255,.62);margin-right:auto;max-width:64ch}
.cidv2-bar .v2btn.dec{border-color:rgba(239,68,68,.45);color:#fca5a5}
.cidv2-bar .v2btn.req{border-color:rgba(245,158,11,.5);color:#f5b455}
.cidv2-bar .v2ovlink{color:rgba(255,255,255,.42)}
@media(max-width:1150px){.cidv2-hdr,.cidv2-bar{left:0}}

.v2themebtn{position:fixed;right:18px;bottom:18px;z-index:59;font-size:11px;color:rgba(255,255,255,.62);
  background:#061422;border:1px solid rgba(255,255,255,.14);border-radius:6px;padding:6px 12px;cursor:pointer;
  font-family:inherit}

.v2scrim{position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:200}
.v2modal{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:201;width:min(680px,92vw);
  background:var(--v2modal);border:1px solid var(--v2teal-line);border-radius:14px;padding:22px 24px}
.v2modal .step{font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--v2tealx)}
.v2modal h4{margin:6px 0 0;font-size:15px;font-weight:600;line-height:1.45;color:var(--v2tx)}
.v2modal .body2{font-size:12.5px;color:var(--v2tx3);margin-top:8px;line-height:1.6}
.v2modal textarea{width:100%;min-height:96px;margin-top:12px;background:var(--v2input);box-sizing:border-box;
  border:1px solid var(--v2line);border-radius:8px;color:var(--v2tx);font-size:12.5px;
  padding:10px 12px;line-height:1.55;resize:vertical;font-family:inherit}
.v2modal .hint{font-size:11px;color:var(--v2mut2);margin-top:7px}
.v2modal .foot{display:flex;gap:9px;margin-top:14px;align-items:center}
.v2modal .prog{font-size:11px;color:var(--v2mut2);margin-right:auto}
.cidv2 :focus-visible{outline:2px solid var(--v2tealx);outline-offset:2px}
@media(prefers-reduced-motion:reduce){.cidv2 *{transition:none!important}}
`;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.id = "cidv2-styles";
  el.textContent = CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

const dotCls = s => "v2dot" + (s === "bad" ? " r" : s === "attn" ? " a" : s === "none" ? " n" : "");

/* ── Sidebar ─────────────────────────────────────────────── */
function Sidebar({ model, actions }) {
  const [open, setOpen] = useState({ 0: true, 1: true });
  const total = model.sidebar.reduce((n, g) => n + g.items.length, 0);
  const needs = model.sidebar.reduce((n, g) => n + g.items.filter(i => i.s === "attn" || i.s === "bad").length, 0);
  return (
    <nav className="cidv2-side">
      <div className="v2sub">{total} assessments &middot; {needs} need you &middot; {total - needs} clean</div>
      <div className="v2legend">
        <span><span className="v2dot a" />needs you</span>
        <span><span className="v2dot" />clean</span>
        <span><span className="v2dot n" />not applicable</span>
      </div>
      {model.sidebar.map((g, gi) => {
        const n = g.items.filter(i => i.s === "attn" || i.s === "bad").length;
        return (
          <div key={gi} className={"v2grp" + (open[gi] ? " open" : "")}>
            <button onClick={() => setOpen(o => ({ ...o, [gi]: !o[gi] }))}>
              <span className="v2caret">&#9656;</span>{g.name}
              <span className={"v2cnt" + (n ? " a" : "")}>{n ? n + " need you" : "clean"}</span>
            </button>
            <div className="v2items">
              {g.items.map((it, ii) => (
                <button key={ii} onClick={() => actions.onJump(it.el)}>
                  <span className={dotCls(it.s)} />{it.n}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div className="cidv2-sidefoot">
        Every assessment is listed, whether or not it found anything.
        <button onClick={actions.onHelp} style={{ fontWeight: 700 }}>Help · ask about this platform</button>
        <div style={{ marginTop: 8, lineHeight: 1.5 }}>Ask Alex, top right, has read this entire case: every document, the full transcript, all the analysis. Ask anything in plain English.</div>
        {model.build && <div style={{ marginTop: 8 }}>Build {model.build}</div>}
      </div>
    </nav>
  );
}

/* ── Stage spine ─────────────────────────────────────────── */
const STAGES = [
  ["Gather", "Mostly the platform\u2019s work. Documents, screening, translation and analysis arrive already assembled. Your part: one request covering everything only the client can answer, then park it while the platform chases."],
  ["Work", "Resolve the AI review\u2019s red lines first (they can close), then record your view on each risk factor (your judgment, once). Run Quick Update after each batch and watch the recommendation move."],
  ["Decide", "Entirely yours. Draft the rationale from the file, record the decision and sign off. Export the audit record and file it."]
];
function StageSpine({ model, actions }) {
  const done = i => model.decided || i < model.stage;
  return (
    <>
      <div className="v2spine">
        {STAGES.map((st, i) => (
          <div key={i} className={"v2sp" + (done(i) ? " done" : i === model.stage ? " on" : "")}>
            <div className="n">
              <span className="num">{done(i) ? "\u2713" : i + 1}</span>{st[0]}
              {!model.decided && i === model.stage && <span className="here">you are here</span>}
              {model.decided && i === 2 && <span className="here" style={{ background: "var(--v2teal-chip)", color: "var(--v2tealx)" }}>decision recorded</span>}
            </div>
            {/* v1_733: the one-line reason the case sits at this stage, so the officer
                is never left to guess WHY ("Work, 2 identity verifications outstanding"). */}
            {!model.decided && i === model.stage && model.stageReason && (
              <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--v2tealx, #84f1ed)", margin: "2px 0 4px" }}>{st[0]}, {model.stageReason}</div>
            )}
            <p>{st[1]}</p>
          </div>
        ))}
      </div>
      <div className="v2rule">Client gave new information &rarr; {actions.regenWord}. You changed your own assessment &rarr; Quick Update.</div>
    </>
  );
}

/* ── This case now ───────────────────────────────────────── */
// bare (v1_673): render only the "This case now" text (no button, no card
// chrome), for the merged Action bar — the primary button moves into the bar's
// unified action row. The tone class is kept so the label colour still signals
// urgency once the box is stripped.
function NowBlock({ model, actions, bare }) {
  const n = model.now;
  if (!n) return null;
  if (bare) {
    return (
      <div className={"v2now" + (n.tone === "go" ? " go" : "")}>
        <div className="k">This case now</div>
        <div className="big">{n.big}</div>
        {n.sm && <div className="sm">{n.sm}</div>}
      </div>
    );
  }
  return (
    <div className={"v2now" + (n.tone === "go" ? " go" : "")} style={n.pay ? { order: -44 } : undefined}>
      <div>
        <div className="k">This case now</div>
        <div className="big">{n.big}</div>
        {n.sm && <div className="sm">{n.sm}</div>}
      </div>
      <div className="v2spacer" />
      {n.btn && <button className="v2btn" disabled={!!n.busy} onClick={n.act}>{n.btn}</button>}
    </div>
  );
}

/* ── Decision board ──────────────────────────────────────── */
function DecisionBoard({ model, actions, onOverride }) {
  const b = model.board;
  const [expanded, setExpanded] = useState(false);
  const slimChecks = b.checks.filter(c => !c.done).length;
  const slimProc = b.procedural.filter(p => !p.done).length;
  if (model.stage < 2 && !model.decided && !expanded) {
    return (
      <div className="v2card" id="panel-decision-v2" style={{ padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 650 }}>Decision board</h3>
          <span className="v2cnt">{slimChecks} question{slimChecks === 1 ? "" : "s"} open · {slimProc} procedural · opens at Decide, or here</span>
          <span style={{ flex: 1 }} />
          <button className="v2btn ghost" onClick={() => setExpanded(true)}>Open</button>
        </div>
      </div>
    );
  }
  const openBlocking = b.checks.filter(c => !c.done).length + b.blockerCount;
  const openProc = b.procedural.filter(c => !c.done).length;
  return (
    <div className="v2card" id="panel-decision-v2">
      <div className="v2hd">
        <h3>Decision board</h3>
        <span className="v2cnt">
          {openBlocking ? openBlocking + " blocking open" : "no blocking open"} &middot; {openProc} procedural open
        </span>
      </div>
      <div className="v2subline">
        What stands between you and sign off. Blocking questions must be answered before any approval; procedural items complete as the platform and the client return.
      </div>

      <div>
        {b.checks.map((c, i) => (
          <div key={"c" + i} className={"v2chk " + (c.done ? "ok" : "no")}>
            <span className="tick">{c.done ? "\u2713" : "\u2717"}</span>
            <span>
              <span className="q">{plainText(c.q)}</span>
              {!c.done && <span className="v2tag b">blocking</span>}
              <span className="a">{plainText(c.detail)}</span>
              {c.officerResolved && <span className="a" style={{ color: "var(--v2teal)" }}>Resolved by {c.officerResolved.by}: {plainText(c.officerResolved.note)}</span>}
            </span>
            {/* v1_737: every blocking check carries an explicit officer action.
                Some checks cannot clear from evidence alone (an unregulated
                company's "Are they regulated?", a source-of-funds answer the
                officer accepts), so Resolve with a reason records the officer's
                mitigation and clears the row. */}
            <span style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", flexShrink: 0 }}>
              {c.el && <button className="jump" onClick={() => actions.onJump(c.el)}>Open</button>}
              {/* v1_743: a check flagged resolvable:false (sanctions screening)
                  cannot be waved through with a note; the officer must screen. */}
              {c.resolvable !== false && !c.done && actions.canTakeAction && (
                <button className="jump" disabled={model.busyResolveCheck === c.key} onClick={() => actions.onResolveCheck(c)}>
                  {model.busyResolveCheck === c.key ? "Saving\u2026" : "Resolve with a reason"}
                </button>
              )}
            </span>
          </div>
        ))}
        {b.procedural.map((c, i) => (
          <div key={"p" + i} className={"v2chk " + (c.done ? "ok" : "no")}>
            <span className="tick">{c.done ? "\u2713" : "\u2717"}</span>
            <span>
              <span className="q">{plainText(c.q)}</span>
              {!c.done && <span className="v2tag p">procedural</span>}
              <span className="a">{plainText(c.detail)}</span>
              {c.officerResolved && <span className="a" style={{ color: "var(--v2teal)" }}>Resolved by {c.officerResolved.by}: {plainText(c.officerResolved.note)}</span>}
            </span>
            {/* v1_743: a procedural check flagged resolvable (the AML policy check
                where no policy is required of the entity) carries the same
                Resolve with a reason control as a blocking check. */}
            <span style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", flexShrink: 0 }}>
              {c.el && <button className="jump" onClick={() => actions.onJump(c.el)}>Open</button>}
              {c.resolvable && !c.done && actions.canTakeAction && (
                <button className="jump" disabled={model.busyResolveCheck === c.key} onClick={() => actions.onResolveCheck(c)}>
                  {model.busyResolveCheck === c.key ? "Saving\u2026" : "Resolve with a reason"}
                </button>
              )}
              {!c.el && !(c.resolvable && !c.done && actions.canTakeAction) && <button className="jump" style={{ visibility: "hidden" }}>Open</button>}
            </span>
          </div>
        ))}
      </div>

      <div className="v2boardfoot">
        <b style={{ color: "var(--v2tx)" }}>Platform state:</b>{" "}
        {b.blockerCount > 0
          ? "blocked. " + b.blockerCount + " blocking item" + (b.blockerCount > 1 ? "s" : "") + " must be resolved before this case can be approved. The decision is referred to you."
          : b.readyForSignoff
            ? "open for decision. Every blocking question on this board is answered; procedural items continue after approval, and the risk factors carry the officer record."
            : "open items remain. Approval waits until every blocking question on this board is answered; procedural items complete after approval."}
      </div>

    </div>
  );
}

/* ── Task lanes ──────────────────────────────────────────── */
function TaskLanes({ model, actions, onStartNotes }) {
  const [showAllMine, setShowAllMine] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const l = model.lanes;
  if (model.stage === 0 && !model.decided && !expanded) {
    return (
      <>
        <div className="v2card" id="panel-todo-v2">
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 650 }}>What to do next</h3>
            <span className="v2cnt">{l.mine.length} item{l.mine.length === 1 ? "" : "s"} queue for Work · nothing needs you while the file is with the client</span>
            <span style={{ flex: 1 }} />
            <button className="v2btn ghost" onClick={() => setExpanded(true)}>Open</button>
          </div>
        </div>
        {l.running.length > 0 && (
          <div className="v2card" id="panel-running-v2">
            <div className="v2hd">
              <h3>Running automatically</h3>
              <span className="v2cnt">{l.running.length} in flight · needs nothing from you</span>
            </div>
            <ul className="v2acts">
              {l.running.map((a, i) => (
                <li key={i}><span>{plainText(a.label)}{a.why && <span className="res">{plainText(a.why)}</span>}</span><span className="stat" style={{ color: "var(--v2tealx)" }}>in flight</span></li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }
  const mine = showAllMine ? l.mine : l.mine.slice(0, 3);
  const hidden = l.mine.length - mine.length;
  return (
    <>
      <div className="v2card" id="panel-todo-v2">
        <div className="v2hd">
          <h3>What to do next</h3>
          <span className="v2cnt">{l.mine.length} you can clear now · {l.waiting.length} with the client</span>
        </div>

        <div className="v2whohd">You can clear these now</div>
        <ul className="v2acts">
          {l.mine.length === 0 && <li><span className="v2lanenote">Nothing outstanding on your side.</span></li>}
          {mine.map((a, i) => (
            <li key={i}>
              <span>{plainText(a.label)}{a.why && <span className="res">{plainText(a.why)}</span>}</span>
              {model.canTakeAction && (a.flow
                ? <button className="go" onClick={onStartNotes}>Start</button>
                : a.act ? <button className="go" onClick={a.act}>{a.btn || "Open"}</button> : null)}
            </li>
          ))}
        </ul>
        {hidden > 0 && <button className="v2morelink" onClick={() => setShowAllMine(true)}>+ {hidden} more</button>}
        {showAllMine && l.mine.length > 3 && <button className="v2morelink" onClick={() => setShowAllMine(false)}>Show fewer</button>}

        {l.waiting.length > 0 && (
          <>
            <div className="v2whohd" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span>{model.roundOpen === false ? "To include in the next request" : "Waiting on the client"} ({l.waiting.length})</span>
              <button className="v2morelink" style={{ padding: 0 }} onClick={() => setShowWaiting(v => !v)}>{showWaiting ? "Hide" : "Show"}</button>
            </div>
            {showWaiting && (
              <ul className="v2acts wait">
                {l.waiting.map((a, i) => (
                  <li key={i}><span>{plainText(a.label)}{a.why && <span className="res">{plainText(a.why)}</span>}</span><span className="stat">{a.stat || "requested"}</span></li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {l.running.length > 0 && (
        <div className="v2card" id="panel-running-v2">
          <div className="v2hd">
            <h3>Running automatically</h3>
            <span className="v2cnt">{l.running.length} in flight · needs nothing from you</span>
          </div>
          <ul className="v2acts">
            {l.running.map((a, i) => (
              <li key={i}><span>{plainText(a.label)}{a.why && <span className="res">{plainText(a.why)}</span>}</span><span className="stat" style={{ color: "var(--v2tealx)" }}>in flight</span></li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/* ── Note flow modal ─────────────────────────────────────── */
function NoteFlowModal({ queue, mode, onSave, onClose }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [idx]);
  if (mode === "override") {
    return (
      <>
        <div className="v2scrim" onClick={onClose} />
        <div className="v2modal">
          <div className="step">Recorded override</div>
          <h4>Approve despite open questions</h4>
          <div className="body2">
            The method exists to make the usual case fast and the unusual case deliberate. If this case is one the method does not anticipate, the decision is still yours: record why approval is right while questions remain open. The override, your reason, and the state of every open item at this moment are written to the case notes before the sign off opens.
          </div>
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Why approval is right despite the open questions&hellip;" />
          <div className="hint">Recorded to the audit trail with your name and today&rsquo;s date, then the normal sign off opens.</div>
          <div className="foot">
            <span className="prog" />
            <button className="v2btn ghost" onClick={onClose}>Cancel</button>
            <button className="v2btn" disabled={busy || !text.trim()} onClick={async () => {
              setBusy(true);
              try { await onSave(null, text.trim(), "override"); onClose(); } finally { setBusy(false); }
            }}>Record and open sign off</button>
          </div>
        </div>
      </>
    );
  }
  if (!queue.length) return null;
  const f = queue[idx];
  const last = idx === queue.length - 1;
  return (
    <>
      <div className="v2scrim" onClick={onClose} />
      <div className="v2modal">
        <div className="step">Risk factor {idx + 1} of {queue.length}</div>
        <h4>{f.text}</h4>
        {f.detail && <div className="body2">{plainText(f.detail)}</div>}
        <textarea value={text} onChange={e => setText(e.target.value)}
          placeholder="What is your view? For example: considered against the register and the interview, proportionate at this tier because&hellip;" />
        <div className="hint">Recorded to the audit trail with your name and today&rsquo;s date. It survives a {"Regenerate"} &amp; Review. Acknowledged is not deleted: the factor stays on the file with your view attached.</div>
        <div className="foot">
          <span className="prog">{idx + 1} of {queue.length}</span>
          <button className="v2btn ghost" onClick={() => last ? onClose() : setIdx(i => i + 1)}>Skip for now</button>
          <button className="v2btn" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              await onSave(f.text, text.trim(), "ack");
              if (last) onClose(); else setIdx(i => i + 1);
            } finally { setBusy(false); }
          }}>{last ? "Save and finish" : "Save and next"}</button>
        </div>
      </div>
    </>
  );
}


/* ── Case briefing: orientation, collapsible once the work begins ── */
function CaseBriefing({ model, actions }) {
  const b = model.briefing;
  // v1_673: for the officer, orientation is the merged Orientation & decision
  // panel in the report body (summary once + grid + positives + screening +
  // recommendation + blockers), so the standalone briefing is suppressed here to
  // avoid printing the summary twice. Non-officer v2 viewers still get it.
  if (model.officerView) return null;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem("cid_brief_" + (model.caseId || ""));
      if (v === "1") return true;
      if (v === "0") return false;
    } catch (_) {}
    return model.stage === 0 && !model.decided;
  });
  const toggle = () => setOpen(o => {
    const n = !o;
    try { localStorage.setItem("cid_brief_" + (model.caseId || ""), n ? "1" : "0"); } catch (_) {}
    return n;
  });
  return (
    <div className="v2card v2brief" id="panel-briefing">
      <div className="v2hd">
        <h3>Case briefing</h3>
        <span className="v2cnt">
          AI view: <b>{b.verdict || "complete"}</b>
          {model.aiOpenRedLines ? " · " + model.aiOpenRedLines + " open red line" + (model.aiOpenRedLines > 1 ? "s" : "") : ""}
          {model.chip ? " · Engine: " + model.chip.text : ""}
        </span>
        <button className="v2morelink" style={{ padding: 0, marginLeft: "auto" }} onClick={toggle}>{open ? "Hide" : "Show"}</button>
      </div>
      {open && <>
        {b.summary && <div className="v2briefsum">{plainText(b.summary)}</div>}
        {b.domains.length > 0 && (
          <div className="v2briefdoms">
            {b.domains.map(([k, v], i) => (
              <div key={i} className={"v2dom" + (/inconsist|gap|unsatisf|concern|opaque|insufficient|noted|partial|findings/i.test(v) ? " warn" : "")}>
                <div className="k">{k}</div><div className="v">{plainText(v)}</div>
              </div>
            ))}
          </div>
        )}
        {b.positives.length > 0 && (
          <div className="v2briefpos">{b.positives.map((p, i) => <span key={i}>&#10003; {plainText(p)}</span>)}</div>
        )}
        <div className="v2briefnote">Orientation only · the working copy, with Draft note and Resolve, lives in Judgment.
          <button className="v2morelink" style={{ display: "inline", padding: 0, marginLeft: 6 }} onClick={() => actions.onJump("panel-ai-review")}>Open it</button>
        </div>
      </>}
    </div>
  );
}

/* ── Header (v2) ─────────────────────────────────────────── */
function HeaderV2({ model, actions, current = "case" }) {
  const tabCls = (k) => (current === k ? "on" : "");
  // The header is fixed and its height follows its content: company, firm and
  // owner wrap at laptop widths and zoom. It publishes its measured height as
  // --cidv2-hdr-h, which the case column, the side navigation, the Ask Alex
  // panel and anchored scrolls read, so nothing sits under it (14/10/2026,
  // production: the update banner and the side navigation were clipped).
  const hdrRef = useRef(null);
  useEffect(() => {
    const el = hdrRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--cidv2-hdr-h", Math.ceil(el.getBoundingClientRect().height) + "px");
    publish();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(publish); ro.observe(el); }
    window.addEventListener("resize", publish);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", publish); root.style.removeProperty("--cidv2-hdr-h"); };
  }, []);
  return (
    <header className="cidv2-hdr" ref={hdrRef}>
      <button className="hback" onClick={actions.onBack}>&larr; Cases</button>
      <div style={{ lineHeight: 1.25 }}>
        <div style={{ fontSize: 13, fontWeight: 650 }}>{model.companyName}</div>
        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.42)" }}>{model.firmName || "compliance dashboard"}{model.owner ? " · owner: " + model.owner : ""}{model.clientReference ? " · your reference: " + model.clientReference : ""}</div>
      </div>
      <div className="htabs">
        <button className={tabCls("case")} onClick={() => actions.onView("report")}>Case</button>
        <button className={tabCls("transcript")} onClick={() => actions.onView("transcript")}>Transcript</button>
        <button className={tabCls("notes")} onClick={() => actions.onView("notes")}>Case notes</button>
        {model.hasUpdates && model.canTakeAction && (
          <button onClick={actions.onMarkSeen} title="Clears the update badge and stamps when you reviewed it. Use Check Responses instead if the client's answers should be pulled into the analysis." style={{ borderColor: "var(--cv2-accent, #0f766e)" }}>
            {model.updateLabel || (model.updateType === 'document' ? 'New doc' : 'New message')} · Mark as seen
          </button>
        )}
        <button onClick={actions.onReviewReport}>Review report &#8599;</button>
        {model.canTakeAction && (model.canAttest || model.canShare || model.canQuestionnaire || model.canMonitoringTier) && (
          <details className="hexp">
            <summary>Actions</summary>
            <div className="hmenu">
              {model.canAttest && <button onClick={actions.onAttest}>Director attestation</button>}
              {model.canShare && <button onClick={actions.onShare}>Share externally</button>}
              {model.canQuestionnaire && <button onClick={actions.onQuestionnaire}>Bank questionnaire</button>}
              {model.canMonitoringTier && <button onClick={actions.onMonitoringTier}>Monitoring tier: {model.monitoringTier}</button>}
            </div>
          </details>
        )}
        <details className="hexp">
          <summary>Exports</summary>
          <div className="hmenu">
            <button onClick={actions.onAuditRecord}>Audit record</button>
            <button onClick={() => actions.onJump("sec-vqf9024-row")}>VQF 902.4 risk profile</button>
            <button onClick={() => actions.onJump("sec-vqf9025-row")}>VQF 902.5 customer profile</button>
          </div>
        </details>
      </div>
      {model.chip && <span className={"hchip " + model.chip.tone} title={model.chip.title || undefined} data-assessment-pending={model.chip.pending ? "1" : undefined}>{model.chip.text}</span>}
      {/* v1_916: the monitoring job cannot check this licence; the full reason is on hover and in the periodic review. */}
      {model.licenceUnmonitored && <span className="hchip md" data-licence-unmonitored="1" title={model.licenceUnmonitored}>Licence not monitored</span>}
      <div className="hspace" />
      {(() => {
        // The next action, always within reach. An officer returning to a case
        // they know should not scroll to act; one meeting it can still read
        // down the page in order, because nothing has moved.
        if (!model.canTakeAction) return null; // v1_687: the header's next-step button is write-only
        // v1_750: the one next action, always within reach. Reads model.na (the
        // shared nextAction function), so it never disagrees with the stage bar.
        const nac = naControl(model, actions);
        if (!nac) return null;
        return <button className="hbtn" disabled={nac.disabled || nac.busy} title={nac.reason || "The next step on this case, from where the file actually is"} onClick={nac.onClick || undefined}>{nac.busy ? "Working\u2026" : nac.label}</button>;
      })()}
      {/* v1_847: shown only where they work. Ask Alex needs the firm's ask_alex
          feature; the scratchpad panel opens on the case page for members who
          may write (MLRO and staff), so read only, and the transcript and notes
          tabs, get no button that would do nothing. */}
      {actions.askAlex && <button className="hbtn" onClick={actions.onAskAlex}>&#10022; Ask Alex</button>}
      {actions.canScratchpad && current === "case" && <button className="hbtn q" onClick={actions.onScratchpad}>Scratchpad</button>}
      {model.theme && <button className="hbtn q" onClick={actions.onToggleTheme}>{model.theme === "light" ? "Dark mode" : "Light mode"}</button>}
      <button className="hbtn q" title="Settings" onClick={actions.onSettings}>&#9881;</button>
      <span className="hwho">{model.who}</span>
    </header>
  );
}

/* Standalone header for the transcript and case-notes tabs: same bar, same
   actions, so the case never loses its chrome when you switch tabs. */
export function CaseHeader({ model, actions, current }) {
  injectCss();
  useEffect(() => {
    const el = document.getElementById("cid-legacy-header");
    if (el) { el.dataset.prevDisplay = el.style.display || ""; el.style.display = "none"; }
    return () => { const e = document.getElementById("cid-legacy-header"); if (e) e.style.display = e.dataset.prevDisplay || ""; };
  }, []);
  return (
    <div className="cidv2">
      <HeaderV2 model={model} actions={actions} current={current === "notes" ? "notes" : "transcript"} />
      <div style={{ height: "var(--cidv2-hdr-h, 50px)" }} />
    </div>
  );
}

/* ── The one next action ──────────────────────────────────────
   Computed once, consumed by the stage bar (which highlights it) and by the
   header (which keeps it reachable without scrolling). One source of truth, so
   the button at the top can never disagree with the one under the spine. */
export function primaryAction(model) {
  if (!model) return null;
  if (model.decided) return { key: "audit", label: "Audit record", fn: "onAuditRecord" };
  if (model.stage === 0) return model.roundOpen === false
    ? { key: "draft", label: "Draft client request", fn: "onDraftRequest" }
    : { key: "check", label: "Check responses", fn: "onCheckResponses" };
  if (model.stage === 1) return { key: "quick", label: "Quick Update", fn: "onQuickUpdate" };
  return { key: "rationale", label: "Draft rationale", fn: "onDraftRationale" };
}

/* v1_750: resolve model.na (from the shared nextAction function) into one
   clickable control. The header's always-reachable button and the stage bar's
   ONE primary button both read this, so they can never disagree. perform runs the
   mapped handler; goto scrolls to and highlights the target panel; wait is
   disabled and carries its reason. A missing handler disables the button rather
   than presenting a live-looking control that does nothing (no silent no-op). */
export function naControl(model, actions) {
  const na = model && model.na;
  if (!na) return null;
  const perform = {
    "reprocess": actions.onRegenerate,
    "draft": actions.onDraftRequest,
    "auto-actions": actions.onRunAutomatedActions,
    "check-responses": actions.onCheckResponses,
    "quick-update": actions.onQuickUpdate,
    "adversarial-review": actions.onAdversarial,
    "draft-rationale": actions.onDraftRationale,
    // v1_779: opens sign off for the decision row 16 means (the case is at Decide),
    // as the stage bar's own button does. Passing the handler bare handed React's
    // click event to openSignoff as the decision; the dialog rendered the event
    // object and the dashboard crashed (React error #31, walk run 15).
    "sign-off": () => actions.onSignoff("Approve"),
    "audit-record": actions.onAuditRecord,
    "second-signoff": actions.onSecondSignoff,
    "welcome": actions.onSendWelcome,
    "terms-settings": actions.onOpenClientTerms,
  };
  let onClick = null;
  // v1_779: never forward the click event into a platform action.
  if (na.kind === "perform") { const fn = perform[na.target]; onClick = fn ? () => fn() : null; }
  else if (na.kind === "goto") onClick = () => actions.onJump(na.target, na.key);
  const disabled = na.kind === "wait" || (na.kind !== "wait" && !onClick);
  return { label: na.label, reason: na.reason, kind: na.kind, onClick, busy: !!model.naBusy, disabled };
}

/* ── Stage actions under the spine ───────────────────────── */
// secondary (v1_673): in the merged Action bar the ONE stage-aware primary
// button is rendered by the bar itself (NowBlock's contextual action), so
// StageActions is the muted palette — all ghost. excludeLabel drops the palette
// button that would duplicate that primary (e.g. "Draft client request" in
// Gather), so the unified action row shows each action once. Standalone (no
// secondary) it still highlights the stage-aware hot button.
export function StageActions({ model, actions, secondary, excludeLabel, excludeKey }) {
  const pa = primaryAction(model);
  const hot = (secondary || model.decided) ? null : pa && pa.key !== "audit" ? pa.key : null;
  const cls = (k) => "v2btn" + (hot === k ? "" : " ghost");
  const btns = [
    { k: "draft", label: "Draft client request", busy: model.busyDraft, busyLabel: "Drafting…", on: actions.onDraftRequest },
    { k: "check", label: "Check responses", busy: model.busyCheck, busyLabel: "Checking…", on: actions.onCheckResponses },
    // v1_804: while a run is active both read "Analysis in progress" and are disabled.
    { k: "regen", label: actions.regenWord, busy: model.busyRegen, busyLabel: "Regenerating…", on: actions.onRegenerate, locked: !!model.engineRunning },
    { k: "quick", label: "Quick Update", busy: model.busyQuick, busyLabel: "Updating…", on: actions.onQuickUpdate, locked: !!model.engineRunning },
    // v1_750: excludeKey drops the ONE palette button the primary next-action
    // button already is, so the secondary group never repeats it; the other three
    // stay, so nothing the officer could do before is lost.
  ].filter(b => (!excludeLabel || b.label !== excludeLabel) && (!excludeKey || b.k !== excludeKey));
  return (
    <div className="v2stageacts">
      {btns.map(b => (
        <button key={b.k} className={cls(b.k)} disabled={b.busy || b.locked} data-engine-locked={b.locked ? "1" : undefined} onClick={b.on}>{b.locked ? "Analysis in progress" : b.busy ? b.busyLabel : b.label}</button>
      ))}
    </div>
  );
}

// Workguide (v1_673) — the "work this case" hint, folded into the Action bar as a
// dismissible strip (flag answer #2). Dismissal persists per case, browser side.
function Workguide({ model }) {
  const key = "cid_wg_" + (model.caseId || "case");
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(key) === "1"; } catch (_) { return false; } });
  if (dismissed) return null;
  if (!(model.stage === 1 && !model.decided && model.aiOpenRedLines > 0)) return null;
  return (
    <div className="v2ab-sec v2workguide" style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{ flex: 1 }}>
        <b>Work this case:</b> resolve or mitigate every red line in the review. Most need your investigation or your recorded sign off, not the client. After each batch, run <b>Quick Update</b> and watch the recommendation move. When it reaches approve, Decide opens with everything on the record.
      </div>
      <button className="v2morelink" style={{ padding: 0, flexShrink: 0 }} title="Dismiss this hint for this case" onClick={() => { try { localStorage.setItem(key, "1"); } catch (_) {} setDismissed(true); }}>Dismiss</button>
    </div>
  );
}

// Action bar (v1_673) — StageSpine + NowBlock + StageActions + Workguide +
// TaskLanes (Running automatically) merged into ONE card. Each piece is a
// sub-section (.v2ab-sec) separated by a light divider; the inner card chrome
// (.v2now, .v2card) is stripped in CSS so nothing reads as a separate panel.
// The bar carries the ONE stage-aware primary button (NowBlock's contextual
// action) followed by the muted secondary palette, in one action row.
function ActionBar({ model, actions, onStartNotes, startNotes }) {
  const now = model.now && model.now.flow ? { ...model.now, act: startNotes } : model.now;
  return (
    <div className="v2actionbar">
      <div className="v2ab-sec"><StageSpine model={model} actions={actions} /></div>
      <div className="v2ab-sec">
        <NowBlock bare model={{ ...model, now }} actions={actions} />
        {/* v1_687: the action row is write-only, so a read-only officer sees the
            "This case now" line and none of the buttons. */}
        {model.canTakeAction && (
          <div className="v2ab-actions">
            {/* v1_750: the ONE primary button. While the case is unpaid the payment
                gate wins (not modelled by the 20 rows); otherwise it is model.na. */}
            {model.isPaymentGate
              ? (now && now.btn && <button className="v2btn" disabled={!!now.busy} onClick={now.act}>{now.btn}</button>)
              : (() => {
                  const nac = naControl(model, actions);
                  if (!nac) return null;
                  return <button className="v2btn" disabled={nac.disabled || nac.busy} title={nac.reason} onClick={nac.onClick || undefined}>{nac.busy ? "Working…" : nac.label}</button>;
                })()}
            <StageActions model={model} actions={actions} secondary excludeLabel={model.isPaymentGate ? (now && now.btn) : null} excludeKey={model.isPaymentGate ? null : model.naExcludeKey} />
          </div>
        )}
      </div>
      <Workguide model={model} />
      <div className="v2ab-sec"><TaskLanes model={model} actions={actions} onStartNotes={onStartNotes} /></div>
    </div>
  );
}

/* ── How the risk tier was derived (v2) ──────────────────── */
function TierV2({ model, actions }) {
  const t = model.tier;
  if (!t) return null;
  const toneOf = r => r === "High" ? "hi" : r === "Medium" ? "md" : "lo";
  const idxOf = r => r === "Low" ? 0 : r === "Medium" ? 1 : 2;
  return (
    <div className="v2card" id="panel-tier-v2">
      <div className="v2hd"><h3>How the risk tier was derived</h3>
        <span className="v2cnt">{t.methodology || "CID-RM-1.0"}</span></div>
      <div className="v2tier"><b className={toneOf(t.tier)}>{t.tier}</b>
        {t.score != null && <span>score {t.score}{t.bands ? " · Low \u2264 " + t.bands.lowMax + ", High \u2265 " + t.bands.highMin : ""}</span>}</div>
      {t.ddLine && <div className="v2tierline">{t.ddLine}</div>}
      <div className="v2bars">
        {t.cats.map((c, i) => (
          [<div key={"l"+i} className="lbl">{c.label}</div>,
           <div key={"s"+i} className="v2seg">{[0,1,2].map(j => <i key={j} className={j === idxOf(c.rating) ? (c.rating === "Low" ? "l" : c.rating === "Medium" ? "m" : "h") : ""} />)}</div>,
           <div key={"b"+i} className="bs">{plainText(c.basis)}</div>]
        ))}
      </div>
      {t.gates.map((g, i) => (
        <div className="v2gate" key={i}>Gate fired · {g.label}.{g.citation ? " " + g.citation + "." : ""} Marked for {g.action === "Decline" ? "decline" : "MLRO referral"}.</div>
      ))}
      {/* v1_851: the escalation that set the tier, named with what it fired on. */}
      {(t.escalations || []).map((e, i) => (
        <div className="v2gate" key={"e" + i} data-escalation={e.trigger}>Escalation met · {plainText(e.label)}{e.evidence ? ": " + plainText(e.evidence) : ""}. Sets {e.tier}.{e.citation ? " " + e.citation + "." : ""}</div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 11 }}>
        <button className="v2btn ghost" onClick={actions.onShowRules}>Show rules evaluated</button>
        {model.canTakeAction && <button className="v2btn ghost" onClick={actions.onAdjustTier}>Propose tier override</button>}
      </div>
      <div className="v2tiernote">A tier override requires a recorded reason and second sign off where your firm has one. It cannot cure untraceable ownership: that has its own lawful route on the blocker. The full derivation, with every rule evaluated whether or not it fired, is one row below.</div>
    </div>
  );
}

/* ── Risk factors (v2) ───────────────────────────────────── */
// v1_673: the ONE Risk factors panel — the Blocker/Advisory split, each factor
// shown once. Blockers are linked up to the Decision headline (not repeated as
// fresh tasks). Collapsed by default; it is the supporting evidence the Decision
// headline distils. The raw High/Medium/Low assessment sub-section was dropped
// as duplicative of this split (v1_673, per officer review).
function RiskFactorsV2({ model, actions, onNote }) {
  const [open, setOpen] = useState({});
  const gathering = model.stage === 0;
  // v1_721 (#4.2): expanded in Gather (the officer is triaging risk), collapsed to
  // a one-line summary in Work/Decide (reference, not the active task), expandable.
  const [panelOpen, setPanelOpen] = useState(gathering);
  const flags = model.flags || [];
  const blockers = model.blockerRows || [];
  const done = flags.filter(f => f.ack).length;
  const raised = flags.filter(f => f.source === "interview").length;
  // v1_721 (#4.3): quick-nav to "Risk factors" opens this panel — it is a v2card,
  // not an EvidenceRow, so it needs its own open-on-jump. When the panel is empty
  // it returns null below, so the jump is a graceful no-op (nothing to open).
  useEffect(() => {
    const h = (e) => { if (e && (e.detail === "panel-rf-v2" || e.detail === "panel-rf-v2-row")) setPanelOpen(true); };
    window.addEventListener("cidv2-open-row", h);
    return () => window.removeEventListener("cidv2-open-row", h);
  }, []);
  if (!flags.length && !blockers.length && !(model.notedFlags || []).length) return null;
  return (
    <div className="v2card" id="panel-rf-v2">
      <div className="v2hd" onClick={() => setPanelOpen(o => !o)} style={{ cursor: "pointer" }}><h3>Risk factors</h3>
        <span className="v2cnt">{flags.length} factor{flags.length === 1 ? "" : "s"} · {flags.length - done} need your view · {done} done{blockers.length ? ` · ${blockers.length} blocker${blockers.length > 1 ? "s" : ""}` : ""}{raised ? ` · ${raised} raised in interview` : ""} · {panelOpen ? "▲" : "▼"}</span></div>
      <div className="v2subline">The Decision headline distils these risk factors; this panel is the supporting evidence and reference behind it.{panelOpen ? "" : " Open to review and note each factor."}</div>
      {panelOpen && <>
      <div className="v2subline">{model.stage === 0
        ? "No judgment is asked of you yet: items are still with the client. You can note early where your view will not change, and the note survives the rebuild."
        : "Ordered as raised. Adding a note records your judgment to the audit trail and survives a rebuild. It does not dismiss the finding: it stays on the file with your view attached."}</div>
      {blockers.map((bl, i) => (
        <div key={"b" + i} className="v2f open">
          <button onClick={() => {}} style={{ cursor: "default" }}>
            <span className="v2pill blk">Blocker, must resolve</span>
            <span className="hl" style={{ color: "var(--v2redtx)" }}>{plainText(bl.label)}</span>
            <span><button className="v2ackbtn" onClick={(e) => { e.stopPropagation(); actions.onJump("panel-decision-headline"); }}>In the Decision headline →</button></span>
          </button>
          {bl.why && <div className="v2fdet" style={{ display: "block" }}>{plainText(bl.why)} Shown in the Decision headline; resolves when the client answers{model.roundOpen ? "; it is part of the open request." : model.lanes && model.lanes.waiting && model.lanes.waiting.length ? "; it will be included in the next client request." : "."}</div>}
        </div>
      ))}
      {flags.map((f, i) => (
        <div key={i} className={"v2f" + (f.ack ? " done" : "") + (open[i] ? " open" : "")}>
          <button onClick={() => setOpen(o => ({ ...o, [i]: !o[i] }))}>
            <span className={"v2pill " + (f.ack ? "done" : "rev")}>{f.ack ? "Noted" : "Advisory, for consideration"}</span>
            {f.source === "interview" && <span className="v2pill" style={{ background: "rgba(132,241,237,0.12)", opacity: 0.85 }} title="Alex raised this inconsistency in the client interview; the client's explanation is below. It is here for your judgement and was not asked again.">Raised in interview</span>}
            <span className="hl">{plainText(f.text)}{f.detail && <span className="more">more</span>}</span>
            {model.canTakeAction && <span><button className="v2ackbtn" onClick={(e) => { e.stopPropagation(); onNote(f); }}>
              {f.ack ? "View your note" : model.stage === 0 ? "Note early" : "Add your note"}</button></span>}
          </button>
          <div className="v2fdet">
            {plainText(f.detail) || "Raised by the analysis; the underlying evidence is on the panels below."}
            {f.ack && <div className="wn">Noted by {f.ack.by} · {new Date(f.ack.at).toLocaleDateString("en-GB")}{f.ack.note ? ": " + plainText(f.ack.note) : ""}</div>}
          </div>
        </div>
      ))}
      {(model.notedFlags || []).length > 0 && <>
        <div className="v2fsplit">Noted by the analysis · context, no action asked</div>
        {model.notedFlags.map((t, i) => <div key={i} className="v2fnoted">{plainText(t)}</div>)}
      </>}
      </>}
    </div>
  );
}

/* ── Your actions before parking (v1_721 #4.4) ───────────────
   The officer's OWN to-do — MLRO red-lines and client-must-respond items that
   otherwise sit far down inside the AI Intelligence Review — surfaced in Gather
   beside Requests to send (order -85, between the stage panel and Requests): the
   internal half of "what to do before parking the case". Deliberately NOT folded
   into Risk factors — that panel is evidence and reference, this is the to-do, and
   the two do not share a container. Jump-to-detail links are kept; where an action
   is "send identity links" the Send / Run-automated-actions control fires here. */
function ActionsBeforeParking({ model, actions }) {
  const list = model.officerActions || [];
  if (model.stage !== 0 || !list.length) return null;
  const KIND = { mlro: "You must action", client: "Client must respond", identity: "Identity check" };
  return (
    <div id="panel-actions-before-parking">
      <div className="v2card">
        <div className="v2hd"><h3>Your actions before parking</h3>
          <span className="v2cnt">{list.length} to do</span></div>
        <div className="v2subline">The internal half of what to do before you park this case; Requests to send below is what the client owes.</div>
        {list.map((a, i) => (
          <div className="v2blocker" key={i} style={{ marginTop: 8 }}>
            <div className="k">{KIND[a.kind] || "Action"}</div>
            <div className="big">{plainText(a.label)}</div>
            {a.why && <div className="sm">{plainText(a.why)}</div>}
            <div className="v2bact" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {a.run === "automated" && model.canTakeAction && (
                <button className="v2btn" onClick={() => actions.onRunAutomatedActions && actions.onRunAutomatedActions()}>Send identity links / run automated actions</button>
              )}
              {a.jumpTo && <button className="v2btn ghost" onClick={() => actions.onJump(a.jumpTo)}>{a.run === "automated" ? "Open ID verification" : "View detail →"}</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Fixed decision bar ──────────────────────────────────── */
function DecisionBarV2({ model, actions, onOverride }) {
  const b = model.board;
  const openBlocking = b.checks.filter(c => !c.done).length + b.blockerCount;
  return (
    <div className="cidv2-bar">
      <span className="btxt">
        {model.decided
          ? (model.pendingSecond
            ? "Decision recorded: " + model.decided + ". The file is with the second signatory for countersignature; nothing further is needed from you until it returns."
            : "The decision is recorded: " + model.decided + ". Export the audit record and file the PDF; the welcome email waits for your deliberate click.")
          : b.readyForSignoff
            ? "Ready to decide. Draft the rationale from the file, then sign off; the recorded words are always yours."
            : openBlocking
              ? openBlocking + " blocking item" + (openBlocking > 1 ? "s" : "") + " must be resolved before approval. Decline and Request more information remain available throughout."
              : "Approval opens when every blocking question on the board is answered; procedural items complete after approval."}
        {!model.decided && !b.readyForSignoff && model.canTakeAction && (
          <button className="v2ovlink" onClick={onOverride}>Approve despite open questions: a recorded override, for the rare case the method does not anticipate</button>
        )}
      </span>
      <button className="v2btn ghost" onClick={actions.onHelp} title="How the platform works: this bar, the zones, any button. Ask Alex (top right) about the case itself." style={{ fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>? Help</button>
      {/* v1_687: sign off controls are write-only \u2014 a read-only officer sees the
          decision status text and Help, never Approve / Decline / Request info. */}
      {!model.decided && model.canTakeAction && <>
        <button className="v2btn ghost" disabled={model.busyRationale} onClick={actions.onDraftRationale}>{model.busyRationale ? "Drafting rationale\u2026" : "Draft rationale from file"}</button>
        <button className="v2btn req" onClick={() => actions.onSignoff("Request Info")}>Request more information</button>
        <button className="v2btn dec" onClick={() => actions.onSignoff("Decline")}>Decline</button>
        <button className="v2btn" disabled={!b.readyForSignoff} onClick={() => actions.onSignoff("Approve")}>Approve</button>
      </>}
      {model.decided && <button className="v2btn ghost" onClick={actions.onHelp}>? Help</button>}
      {model.decided && <button className="v2btn ghost" onClick={() => actions.onJump("panel-decision")}>Open the decision record</button>}
    </div>
  );
}

/* ── Standalone blockers ─────────────────────────────────── */
function Blockers({ model, actions }) {
  const list = model.board.blockers || [];
  if (!list.length) return null;
  return (
    <>{list.map((bl, i) => (
      <div className="v2blocker" key={i}>
        <div className="k">Blocking this case</div>
        <div className="big">{plainText(bl.label)}</div>
        {bl.why && <div className="sm">{plainText(bl.why)}</div>}
        {bl.act && <div className="v2bact"><button className="v2btn ghost" onClick={bl.act}>{bl.btn || "Open"}</button></div>}
      </div>
    ))}</>
  );
}

/* ── Main export ─────────────────────────────────────────── */
export default function CaseViewV2({ model, actions }) {
  injectCss();
  // Belt and braces (v1_408): the legacy header is also gated inline, but the
  // v2 view enforces the hide at runtime so no styling ambiguity can leave two
  // headers on screen. Restored the moment the v2 view unmounts.
  useEffect(() => {
    const el = document.getElementById("cid-legacy-header");
    if (el) { el.dataset.prevDisplay = el.style.display || ""; el.style.display = "none"; }
    return () => { const e = document.getElementById("cid-legacy-header"); if (e) e.style.display = e.dataset.prevDisplay || ""; };
  }, []);
  const [modal, setModal] = useState(null); // null | {mode:'ack',queue} | {mode:'override'}
  const startNotes = () => {
    const queue = model.unackedFlags;
    if (!queue.length) return;
    setModal({ mode: "ack", queue });
  };
  return (
    <div className="cidv2 cidv2-main" data-theme={model.theme} data-stage={stageAttr({ decideStage: model.decideStage, stage: model.stage })}>
      <HeaderV2 model={model} actions={actions} current="case" />
      <Sidebar model={model} actions={actions} />

      {/* v1_673: for the officer the whole orientation strip is the merged
          Orientation & decision panel in the report body; suppress it here. */}
      {!model.officerView && (model.briefing ? (
        <CaseBriefing model={model} actions={actions} />
      ) : (model.aiVerdict || model.chip) && (
        <div className="v2verdict">
          {model.aiVerdict && <>AI view: <b>{model.aiVerdict}</b></>}
          {model.aiVerdict && model.chip && <span> · </span>}
          {model.chip && <>Engine: <b>{model.chip.text}</b></>}
        </div>
      ))}
      {false && (
        <div className="v2card v2brief" id="panel-briefing-legacy">
          <div className="v2hd">
            <h3>Case briefing</h3>
            <span className="v2cnt">
              AI view: <b>{model.briefing.verdict || "complete"}</b>
              {model.aiOpenRedLines ? " · " + model.aiOpenRedLines + " open red line" + (model.aiOpenRedLines > 1 ? "s" : "") : ""}
              {model.chip ? " · Engine: " + model.chip.text : ""}
            </span>
          </div>
          {model.briefing.summary && <div className="v2briefsum">{model.briefing.summary}</div>}
          {model.briefing.domains.length > 0 && (
            <div className="v2briefdoms">
              {model.briefing.domains.map(([k, v], i) => (
                <div key={i} className={"v2dom" + (/inconsist|gap|unsatisf|concern|opaque|insufficient|noted|partial|findings/i.test(v) ? " warn" : "")}>
                  <div className="k">{k}</div><div className="v">{plainText(v)}</div>
                </div>
              ))}
            </div>
          )}
          {model.briefing.positives.length > 0 && (
            <div className="v2briefpos">{model.briefing.positives.map((p, i) => <span key={i}>&#10003; {plainText(p)}</span>)}</div>
          )}
          <div className="v2briefnote">Orientation only · the working copy, with Draft note and Resolve, lives in Judgment.
            <button className="v2morelink" style={{ display: "inline", padding: 0, marginLeft: 6 }} onClick={() => actions.onJump("panel-ai-review")}>Open it</button>
          </div>
        </div>
      )}
      {/* v1_673: the three decorative zone labels ("Working the case", "Judgment",
          "Evidence") are retired. The column is now fenced by two structural
          section headers (VERIFICATION, EVIDENCE & RECORD) rendered in the report
          body at orders -11 / -1; the working band above them needs no label. */}
      {/* v1_673: StageSpine + NowBlock + StageActions + workguide + TaskLanes
          (with Running automatically) are now ONE Action bar (order -90). */}
      <ActionBar model={model} actions={actions} onStartNotes={startNotes} startNotes={startNotes} />
      <ActionsBeforeParking model={model} actions={actions} />
      <DecisionBoard model={model} actions={actions} onOverride={() => setModal({ mode: "override" })} />
      <RiskFactorsV2 model={model} actions={actions} onNote={(f) => setModal({ mode: "ack", queue: [{ text: f.text, detail: f.detail }] })} />
      <DecisionBarV2 model={model} actions={actions} onOverride={() => setModal({ mode: "override" })} />

      {modal && (
        <NoteFlowModal
          mode={modal.mode}
          queue={modal.queue || []}
          onClose={() => setModal(null)}
          onSave={async (text, note, kind) => {
            if (kind === "override") { await actions.onOverride(note); return; }
            await actions.onAck(text, note);
          }}
        />
      )}
      {/* v1_673: the trailing "Evidence · open only what you need" zone label is
          retired with the other two — the column is fenced by the structural
          VERIFICATION / EVIDENCE & RECORD headers (orders -11 / -1) in the report
          body, so the native cards need no decorative labels. */}
    </div>
  );
}

/* ── Evidence row ────────────────────────────────────────────
   Wraps an existing panel in a collapsed one-line verdict. The panel
   itself renders unchanged inside; when the row is closed it stays
   mounted but hidden, so panel state and handlers are untouched.
   With active false (classic view) the wrapper vanishes entirely.
   A 'cidv2-open-row' window event opens the row before a jump lands. */
export function EvidenceRow({ id, active, meta, children }) {
  const [open, setOpen] = useState(() => {
    if (meta && meta.persistKey) {
      try {
        const v = localStorage.getItem("cid_row_" + meta.persistKey);
        if (v === "1") return true;
        if (v === "0") return false;
      } catch (_) {}
    }
    // Auto-open on the officer's FIRST view of this row for this case, then stay
    // collapsed on every return (v1_673 money map). The seen marker is scoped by
    // the autoOnce key (officer's browser + case id, set by the caller); a return
    // visit finds it set and starts collapsed. The marker is written in an effect,
    // not here, so render stays pure.
    if (meta && meta.autoOnce) {
      try { return localStorage.getItem("cid_seen_" + meta.autoOnce) !== "1"; } catch (_) { return true; }
    }
    return !!(meta && meta.defaultOpen);
  });
  const toggle = () => setOpen(o => {
    const n = !o;
    if (meta && meta.persistKey) { try { localStorage.setItem("cid_row_" + meta.persistKey, n ? "1" : "0"); } catch (_) {} }
    return n;
  });
  useEffect(() => {
    if (meta && meta.autoOnce) {
      try { localStorage.setItem("cid_seen_" + meta.autoOnce, "1"); } catch (_) {}
    }
  }, [meta && meta.autoOnce]);
  useEffect(() => {
    const h = (e) => { if (e && (e.detail === id || e.detail === id + '-row')) setOpen(true); };
    window.addEventListener('cidv2-open-row', h);
    return () => window.removeEventListener('cidv2-open-row', h);
  }, [id]);
  if (!active) return children;
  injectCss();
  const m = meta || { n: id, v: '', s: 'ok' };
  return (
    <div id={id + "-row"} className={"cidv2 v2rowwrap " + (m.s || "ok")} data-theme={(meta && meta.theme) || "dark"} style={{ scrollMarginTop: 80, order: (meta && meta.order) || undefined }}>
      <button className="v2rowbtn" onClick={toggle}>
        <span className={dotCls(m.s)} style={{ marginTop: 6 }} />
        <span className="nm">{m.n}</span>
        <span className="vd">{plainText(m.v)}</span>
        <span className="oc">{open ? "Hide" : "Open"}</span>
      </button>
      <div className="v2rowbody" style={{ display: open ? "block" : "none" }}>{children}</div>
    </div>
  );
}
